// src/utils/estimateEmailChange.js — doced-8f3a61c2 (Document editor, Stage 5f-2: change the customer's email)
//
// WHERE THE EMAIL GOES. sendQuoteEmail sends ONLY to the estimate's own copy of the address
// (customerDetails.email, else the older customerEmail), taken when the customer was chosen. The customer
// record is never read at send time, and changing it never changes an estimate that already exists.
// That server rule is unchanged: these helpers only change what is SAVED on the estimate (and, if asked,
// the customer record), so the next send reads the new address from the saved estimate as always.
//
// WHO MAY ALSO CHANGE THE CUSTOMER RECORD (founder decisions 2026-10-28): Owner / Admin / Manager, Office
// Staff, and a Field Technician where the company lets technicians manage customers — the roles
// firestore.rules lets edit a customer. Sales Consultants change the address on the estimate only
// ("This changes the address on this estimate only"); a refused customer write is reported, never hidden.
//
// Pure except changeEstimateEmail, which takes its Firestore functions as arguments.

import { parseCcInput } from './estimateSend';
import { HIGH_LEVEL_ROLES, canTechManageCustomers } from './permissions';
import { fingerprintOf, saveWithConflictCheck } from './estimateConflict';

export const ESTIMATE_ONLY_NOTE = 'This changes the address on this estimate only.';
export const OTHERS_KEEP_NOTE = 'Other estimates and invoices keep the address they were made with.';
export const RECORD_REFUSED = 'Updated on this estimate only; the customer record couldn’t be changed.';

/** The address on the estimate itself — what the email will be sent to. Never the customer record's. */
export const estimateEmailOf = (q) => (q && ((q.customerDetails && q.customerDetails.email) || q.customerEmail)) || '';

/** The problem with a typed address (one address, the same format rule as CC), or null. */
export function emailProblem(text) {
    const s = String(text || '').trim();
    if (!s) return 'Enter an email address.';
    if (/[\s,;]/.test(s)) return 'Enter one email address.';
    return parseCcInput(s).problem;
}

/** May this person also change the customer record? (mirrors firestore.rules customers update, by role) */
export function canUpdateCustomerRecord(profile, companySettings) {
    const role = profile && profile.role;
    if (!role) return false;
    if (HIGH_LEVEL_ROLES.includes(role) || role === 'Office Staff') return true;
    return canTechManageCustomers(profile, companySettings);
}

/** The estimate's address differs from the customer record's (both present, ignoring case and spaces). */
export function differsFromRecord(estimateEmail, customer) {
    const a = String(estimateEmail || '').trim().toLowerCase();
    const b = String((customer && customer.email) || '').trim().toLowerCase();
    return !!a && !!b && a !== b;
}

const sameAddress = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
export const RECORD_UPDATED = 'Customer record updated.';

/**
 * What Save would change (founder rule 2026-10-28): the estimate when the address differs from the estimate's;
 * the customer record when that is offered, ticked, and the address differs from the record's. Neither → Save off.
 * → { estimate: boolean, record: boolean, any: boolean }
 */
export function plannedChanges({ email, estimateEmail, recordEmail, offerRecord, alsoRecord }) {
    const estimate = !sameAddress(email, estimateEmail);
    const record = !!(offerRecord && alsoRecord && !sameAddress(email, recordEmail));
    return { estimate, record, any: estimate || record };
}

/** The editor's state with a new estimate address (saved later by the page's own Save). */
export function withEstimateEmail(quoteData, email) {
    return { ...quoteData, customerDetails: { ...(quoteData.customerDetails || {}), email: String(email).trim() } };
}

/** The merge-write that changes the SAVED estimate's address — a person's edit, so it stamps both clocks. */
export function estimateEmailWrite(stored, email, serverTimestamp) {
    return {
        customerDetails: { ...((stored && stored.customerDetails) || {}), email: String(email).trim() },
        updatedAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
    };
}

/**
 * Send page: change the address on the SAVED estimate (conflict-checked), then the customer record if asked.
 * deps = { runTransaction, db, quoteRef, customerRef (or null), updateDoc, serverTimestamp }
 * recordEmail = the customer record's current address (so an unchanged record is never rewritten).
 * → { ok: true, record: 'updated' | 'skipped' | 'refused', estimate?: 'unchanged', message } | { ok: false, message }
 */
export async function changeEstimateEmail({ stored, email, alsoRecord, recordEmail }, deps) {
    const { runTransaction, db, quoteRef, customerRef, updateDoc, serverTimestamp } = deps;
    const value = String(email).trim();
    // Only the customer record differs (the estimate already has this address): write only the record.
    if (sameAddress(value, estimateEmailOf(stored))) {
        if (!alsoRecord || !customerRef || sameAddress(value, recordEmail)) return { ok: true, record: 'skipped', estimate: 'unchanged', message: '' };
        try {
            await updateDoc(customerRef, { email: value, updatedAt: serverTimestamp() });
            return { ok: true, record: 'updated', estimate: 'unchanged', message: RECORD_UPDATED };
        } catch (e) {
            return { ok: false, message: 'The customer record couldn’t be changed. You may not have permission to edit this customer.' };
        }
    }
    try {
        await saveWithConflictCheck({ runTransaction, db, ref: quoteRef, write: estimateEmailWrite(stored, value, serverTimestamp), isNew: false, baseline: fingerprintOf(stored) });
    } catch (e) {
        if (e && e.kind) return { ok: false, message: e.message.replace(/Nothing was saved\./, 'The address was not changed.') };
        if (e && e.code === 'permission-denied') return { ok: false, message: 'Spatchy didn’t allow this change to the estimate.' };
        return { ok: false, message: 'The address couldn’t be changed. Check your connection and try again.' };
    }
    if (!alsoRecord || !customerRef || sameAddress(value, recordEmail)) return { ok: true, record: 'skipped', message: '' };
    try {
        // Only the email (and the record's own updatedAt) — as the customer form's merge would change them.
        await updateDoc(customerRef, { email: value, updatedAt: serverTimestamp() });
        return { ok: true, record: 'updated', message: '' };
    } catch (e) {
        return { ok: true, record: 'refused', message: RECORD_REFUSED };
    }
}

/** Editor: the customer record only (the estimate's own address is an ordinary unsaved edit there). */
export async function updateCustomerRecordEmail({ email }, { customerRef, updateDoc, serverTimestamp }) {
    try {
        await updateDoc(customerRef, { email: String(email).trim(), updatedAt: serverTimestamp() });
        return { record: 'updated', message: '' };
    } catch (e) {
        return { record: 'refused', message: 'The customer record couldn’t be changed — the new address is only on this estimate.' };
    }
}
